import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Clock, GitCommit, Wrench, AlertTriangle, ChevronRight, Activity } from "lucide-react";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";

type Status = "queued" | "claimed" | "running" | "success" | "failed" | "cancelled";

type NarrationEvent = {
  at: string;
  kind: "step" | "tool" | "commit" | "error" | "info";
  text: string;
};

type RunListItem = {
  id: string;
  feedbackId: string;
  status: Status;
  source: string;
  claimedBy: string | null;
  narration: NarrationEvent[];
  commitSha: string | null;
  summary: string | null;
  errorMessage: string | null;
  queuedAt: string | null;
  claimedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  feedbackMessage: string;
  feedbackCategory: string;
};

type RunDetail = RunListItem;

function formatDuration(start: string | null, end: string | null) {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const seconds = Math.max(0, Math.floor((e - s) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const r = seconds % 60;
  return `${m}m ${r}s`;
}

function relativeTime(iso: string | null) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function statusBadge(status: Status, animated: boolean) {
  const base = "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium";
  switch (status) {
    case "queued":
      return <span className={`${base} bg-gray-100 text-gray-700`}><Clock className="w-3 h-3" /> queued</span>;
    case "claimed":
      return <span className={`${base} bg-blue-100 text-blue-700`}><Activity className="w-3 h-3" /> claimed</span>;
    case "running":
      return <span className={`${base} bg-amber-100 text-amber-800`}>{animated ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} running</span>;
    case "success":
      return <span className={`${base} bg-emerald-100 text-emerald-800`}><CheckCircle2 className="w-3 h-3" /> success</span>;
    case "failed":
      return <span className={`${base} bg-red-100 text-red-800`}><XCircle className="w-3 h-3" /> failed</span>;
    case "cancelled":
      return <span className={`${base} bg-gray-200 text-gray-600`}><XCircle className="w-3 h-3" /> cancelled</span>;
    default:
      return <span className={base}>{status}</span>;
  }
}

function kindIcon(kind: NarrationEvent["kind"]) {
  switch (kind) {
    case "step": return <ChevronRight className="w-3 h-3 mt-0.5 text-gray-400" />;
    case "tool": return <Wrench className="w-3 h-3 mt-0.5 text-blue-500" />;
    case "commit": return <GitCommit className="w-3 h-3 mt-0.5 text-emerald-600" />;
    case "error": return <AlertTriangle className="w-3 h-3 mt-0.5 text-red-500" />;
    default: return <Activity className="w-3 h-3 mt-0.5 text-gray-400" />;
  }
}

function LogViewer({ events, status }: { events: NarrationEvent[]; status: Status }) {
  const isLive = status === "queued" || status === "claimed" || status === "running";
  return (
    <div className="bg-gray-950 text-gray-100 rounded-lg p-3 font-mono text-xs leading-relaxed min-h-[300px] max-h-[600px] overflow-y-auto">
      {events.length === 0 ? (
        <div className="text-gray-500 italic">
          {isLive ? "Waiting for daemon output…" : "No narration recorded."}
        </div>
      ) : (
        <div className="space-y-1">
          {events.map((e, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-gray-500 shrink-0 w-20 truncate">{new Date(e.at).toLocaleTimeString()}</span>
              <span className="shrink-0">{kindIcon(e.kind)}</span>
              <span className={`whitespace-pre-wrap break-words ${
                e.kind === "error" ? "text-red-300" :
                e.kind === "commit" ? "text-emerald-300" :
                e.kind === "tool" ? "text-blue-300" :
                "text-gray-100"
              }`}>{e.text}</span>
            </div>
          ))}
          {isLive && (
            <div className="flex gap-2 items-center text-amber-300 pt-2">
              <Loader2 className="w-3 h-3 animate-spin" /> live
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RunDetailView({ runId }: { runId: string }) {
  const { data, isLoading } = useQuery<RunDetail>({
    queryKey: ["/api/admin/triage/runs", runId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/triage/runs/${runId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load run");
      return res.json();
    },
    refetchInterval: (q) => {
      const status = (q.state.data as RunDetail | undefined)?.status;
      if (status === "queued" || status === "claimed" || status === "running") return 2000;
      return false;
    },
  });

  if (isLoading) return <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading run…</div>;
  if (!data) return <div className="text-sm text-gray-500">Run not found.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {statusBadge(data.status, true)}
        <span className="text-xs text-gray-500">id {data.id.slice(0, 8)}</span>
        <span className="text-xs text-gray-500">duration {formatDuration(data.startedAt ?? data.claimedAt ?? data.queuedAt, data.endedAt)}</span>
        {data.claimedBy && <span className="text-xs text-gray-500">on {data.claimedBy}</span>}
        <Link href="/admin/triage" className="ml-auto text-xs text-blue-600 hover:underline">← all runs</Link>
      </div>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="text-xs uppercase text-gray-500">Feedback</div>
          <div className="text-sm whitespace-pre-wrap">{data.feedbackMessage}</div>
          <div className="text-xs text-gray-500">
            <Link href={`/admin/feedback`} className="text-blue-600 hover:underline">open in /admin/feedback</Link> · {data.feedbackCategory}
          </div>
        </CardContent>
      </Card>

      {data.commitSha && (
        <div className="text-sm flex items-center gap-2">
          <GitCommit className="w-4 h-4 text-emerald-600" />
          <a href={`https://github.com/marcos1289/marketlink/commit/${data.commitSha}`} target="_blank" rel="noreferrer" className="font-mono text-emerald-700 hover:underline">{data.commitSha.slice(0, 7)}</a>
          {data.summary && <span className="text-gray-700 truncate">— {data.summary.split("\n")[0]}</span>}
        </div>
      )}

      {data.errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 whitespace-pre-wrap font-mono">
          {data.errorMessage}
        </div>
      )}

      <div>
        <div className="text-xs uppercase text-gray-500 mb-1">Live log</div>
        <LogViewer events={(data.narration ?? []) as NarrationEvent[]} status={data.status} />
      </div>
    </div>
  );
}

function RunListView({ onSelect }: { onSelect: (id: string) => void }) {
  const { data, isLoading } = useQuery<RunListItem[]>({
    queryKey: ["/api/admin/triage/runs"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/triage/runs?limit=100`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load runs");
      return res.json();
    },
    refetchInterval: 3000,
  });

  const counts = useMemo(() => {
    const c = { running: 0, queued: 0, success: 0, failed: 0 };
    for (const r of data ?? []) {
      if (r.status === "running" || r.status === "claimed") c.running++;
      else if (r.status === "queued") c.queued++;
      else if (r.status === "success") c.success++;
      else if (r.status === "failed") c.failed++;
    }
    return c;
  }, [data]);

  if (isLoading) return <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading runs…</div>;
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-gray-600">
          No triage runs yet. Set <code className="bg-gray-100 px-1">TRIAGE_TARGET=mac</code> on the server and start the daemon (<code className="bg-gray-100 px-1">node scripts/triage-daemon.mjs</code>) to begin.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-3 text-sm text-gray-700">
        {counts.running > 0 && <span><Activity className="w-3 h-3 inline mr-1 text-amber-500" />{counts.running} running</span>}
        {counts.queued > 0 && <span><Clock className="w-3 h-3 inline mr-1 text-gray-500" />{counts.queued} queued</span>}
        <span><CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-500" />{counts.success} success</span>
        {counts.failed > 0 && <span><XCircle className="w-3 h-3 inline mr-1 text-red-500" />{counts.failed} failed</span>}
      </div>
      <div className="space-y-2">
        {data.map((r) => (
          <Card
            key={r.id}
            className="cursor-pointer hover:bg-gray-50 transition"
            onClick={() => onSelect(r.id)}
          >
            <CardContent className="p-3 flex items-start gap-3">
              <div className="shrink-0 mt-0.5">{statusBadge(r.status, true)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 truncate">{r.feedbackMessage || "(empty feedback)"}</div>
                <div className="text-xs text-gray-500 flex gap-3 mt-0.5">
                  <span>{relativeTime(r.queuedAt)}</span>
                  <span>duration {formatDuration(r.startedAt ?? r.claimedAt ?? r.queuedAt, r.endedAt)}</span>
                  {r.commitSha && <span className="font-mono text-emerald-700">{r.commitSha.slice(0, 7)}</span>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function AdminTriage({ runId }: { runId?: string }) {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  // Re-fetch the detail row when runId changes so the log resets.
  useEffect(() => {
    if (runId) qc.invalidateQueries({ queryKey: ["/api/admin/triage/runs", runId] });
  }, [runId, qc]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4">
          <Link href="/admin/feedback" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-semibold">Triage runs</h1>
          <span className="text-sm text-gray-500 ml-2">Mac daemon · live</span>
        </div>
        {runId ? (
          <RunDetailView runId={runId} />
        ) : (
          <RunListView onSelect={(id) => setLocation(`/admin/triage/${id}`)} />
        )}
      </div>
    </div>
  );
}
